import { Link } from "react-router-dom";
import { useSEO } from "../lib/useSEO";
import PageHero from "../components/layout/PageHero";
import SectionHeader from "../components/SectionHeader";
import Icon from "../components/Icon";
import { PathwayEnquiryForm } from "../components/Forms";
import { Reveal, Dots } from "../components/motion/Reveal";

const SEGMENT1_IMG = "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=1200&q=80";
const SEGMENT2_IMG = "https://images.unsplash.com/photo-1525625293386-3f8f99389edd?auto=format&fit=crop&w=1200&q=80";

const SEGMENTS = [
  {
    id: "segment-foundation",
    badge: "Segment 01",
    title: "Foundation Pathways for O-Level Students",
    intro: "Many students who complete GCE O Levels aspire to study at world-class universities but may not meet direct entry requirements. Foundation programmes offer them a structured route to success.",
    image: SEGMENT1_IMG,
    imageAlt: "O-Level students studying together",
    partnersTitle: "Partner Options",
    partners: [
      { name: "Kings Education", slug: "kings-education" },
      { name: "Study Group", slug: "study-group" },
    ],
    partnerPoints: [
      "Reputed international pathway providers offering Foundation Courses in the UK, Australia, New Zealand, Canada, and beyond.",
      "Tailored curriculum focused on academic subjects, study skills, and English proficiency.",
    ],
    benefits: [
      "Guaranteed progression to a range of universities upon successful completion.",
      "Dedicated support in adapting to international academic and cultural environments.",
      "Builds confidence, independence, and a strong academic base.",
    ],
  },
  {
    id: "segment-diploma",
    badge: "Segment 02",
    title: "Diploma & Higher Diploma Pathways via PEIs",
    intro: "For students seeking flexibility, affordability, and an internationally recognized qualification, Singapore's Private Education Institutes (PEIs) provide an attractive alternative.",
    image: SEGMENT2_IMG,
    imageAlt: "Singapore skyline, a global education hub",
    partnersTitle: "Partner Options",
    partners: [
      { name: "BITC International College", slug: "bitc-international-college" },
      { name: "AceTek College", slug: "acetek-college" },
    ],
    partnerPoints: [
      "Diploma / Higher Diploma in Singapore in fields such as Hospitality, Business, Logistics, IT, and more.",
      "Progression opportunities to universities in the UK, New Zealand, Canada, and other destinations with advanced standing (credit exemptions).",
    ],
    benefits: [
      "Cost-effective first stage of international education.",
      "Opportunity to study in Singapore, a global education hub.",
      "Direct progression routes through articulation agreements with overseas universities.",
      "Exposure to a multicultural learning environment that prepares students for global careers.",
    ],
  },
];

const WHY_PATHWAYS = [
  { icon: "Route", title: "Bridging Academic Gaps", text: "Builds confidence, independence, and a strong academic base." },
  { icon: "Globe2", title: "Global Partnerships", text: "Access to top-ranked universities via our partner network." },
  { icon: "ShieldCheck", title: "Official & Transparent Channels", text: "Every admission is facilitated through official and legal pathways." },
  { icon: "Target", title: "Future-Proof Careers", text: "Graduates gain international qualifications recognized worldwide, improving employability." },
];

export default function Pathways() {
  useSEO(
    "Pathway Programmes — Suvera Academy",
    "Foundation programmes for O-Level graduates and Diploma & Higher Diploma pathways via Singapore PEIs — clear routes into universities worldwide."
  );
  return (
    <main data-testid="pathways-page">
      <PageHero
        eyebrow="Pathway Programmes"
        title="Pathway Programmes"
        description="At Suvera Academy, we believe that every learner deserves a clear and achievable route to higher education, regardless of their starting point. Our Pathway and Foundation Programmes bridge academic gaps, enhance language proficiency, and prepare students for successful progression into universities across the world."
        breadcrumbs={[{ label: "Home", to: "/" }, { label: "Pathway Programmes" }]}
        testid="pathways-hero"
      />

      {/* Two segments */}
      <section className="mx-auto max-w-[1440px] px-4 py-16 sm:px-6 lg:px-10 lg:py-24" data-testid="segments-overview">
        <SectionHeader
          chapter="Chapter 01"
          eyebrow="Who It's For"
          title="Two Distinct Pathways, One Goal"
          description="We focus on two distinct market segments — choose the route that matches where you are today."
        />
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <Reveal>
            <a href="#segment-foundation" className="group block h-full rounded-2xl border border-slate-200 bg-white p-8 transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1.5 hover:border-navy/30 hover:shadow-[0_20px_50px_-16px_rgba(13,24,51,0.18)]" data-testid="segment-card-foundation">
              <span className="font-mono-accent text-xs uppercase tracking-[0.25em] text-dot-red">Segment 01</span>
              <h3 className="mt-3 font-display text-2xl font-semibold text-navy-dark">O-Level Graduates</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-600 sm:text-base">
                Students who have completed GCE O Levels and wish to pursue Foundation Programmes with leading pathway providers such as Kings Education and Study Group.
              </p>
              <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-navy transition-colors duration-200 group-hover:text-dot-blue">
                Explore This Pathway <Icon name="ArrowRight" className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
              </span>
            </a>
          </Reveal>
          <Reveal delay={0.1}>
            <a href="#segment-diploma" className="group block h-full rounded-2xl border border-slate-200 bg-white p-8 transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1.5 hover:border-navy/30 hover:shadow-[0_20px_50px_-16px_rgba(13,24,51,0.18)]" data-testid="segment-card-diploma">
              <span className="font-mono-accent text-xs uppercase tracking-[0.25em] text-dot-blue">Segment 02</span>
              <h3 className="mt-3 font-display text-2xl font-semibold text-navy-dark">Local &amp; International Diploma Seekers</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-600 sm:text-base">
                Students who choose to study a Diploma or Higher Diploma with established Private Education Institutes (PEIs) in Singapore, then progress to partner universities overseas.
              </p>
              <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-navy transition-colors duration-200 group-hover:text-dot-blue">
                Explore This Pathway <Icon name="ArrowRight" className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
              </span>
            </a>
          </Reveal>
        </div>
      </section>

      {/* Segment detail sections */}
      {SEGMENTS.map((s, si) => (
        <section key={s.id} id={s.id} className={`scroll-mt-24 ${si % 2 ? "" : "bg-sand"}`} data-testid={s.id}>
          <div className="mx-auto max-w-[1440px] px-4 py-16 sm:px-6 lg:px-10 lg:py-24">
            <div className={`grid grid-cols-1 items-center gap-12 lg:grid-cols-12`}>
              <div className={`lg:col-span-6 ${si % 2 ? "lg:order-2" : ""}`}>
                <SectionHeader chapter={s.badge} eyebrow={si === 0 ? "Foundation" : "Diploma & Higher Diploma"} title={s.title} />
                <Reveal>
                  <p className="-mt-6 text-base leading-relaxed text-slate-600 sm:text-lg">{s.intro}</p>
                </Reveal>
                <Reveal delay={0.08}>
                  <h3 className="mt-8 font-mono-accent text-[10px] uppercase tracking-[0.22em] text-slate-400">{s.partnersTitle}</h3>
                  <div className="mt-3 flex flex-wrap gap-2.5">
                    {s.partners.map((p) => (
                      <Link
                        key={p.slug}
                        to={`/institutes/${p.slug}`}
                        className="inline-flex items-center gap-1.5 rounded-full border border-navy/20 bg-white px-4 py-2 text-sm font-semibold text-navy transition-colors duration-200 hover:bg-navy hover:text-white"
                        data-testid={`pathway-partner-${p.slug}`}
                      >
                        {p.name}
                        <Icon name="ArrowUpRight" className="h-3.5 w-3.5" />
                      </Link>
                    ))}
                  </div>
                  <ul className="mt-4 space-y-2.5">
                    {s.partnerPoints.map((p) => (
                      <li key={p} className="flex items-start gap-2.5 text-sm leading-relaxed text-slate-600">
                        <Icon name="CheckCircle2" className="mt-0.5 h-4 w-4 shrink-0 text-dot-blue" />
                        {p}
                      </li>
                    ))}
                  </ul>
                </Reveal>
                <Reveal delay={0.14}>
                  <h3 className="mt-8 font-mono-accent text-[10px] uppercase tracking-[0.22em] text-slate-400">Key Benefits</h3>
                  <ul className="mt-3 space-y-2.5">
                    {s.benefits.map((b) => (
                      <li key={b} className="flex items-start gap-2.5 text-sm leading-relaxed text-slate-700">
                        <Icon name="CheckCircle2" className="mt-0.5 h-4 w-4 shrink-0 text-dot-green" />
                        {b}
                      </li>
                    ))}
                  </ul>
                </Reveal>
              </div>
              <div className={`lg:col-span-6 ${si % 2 ? "lg:order-1" : ""}`}>
                <Reveal delay={0.12}>
                  <div className="img-frame">
                    <img src={s.image} alt={s.imageAlt} className="max-h-[60vh] w-full object-cover lg:max-h-[480px]" loading="lazy" />
                  </div>
                </Reveal>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* Why choose */}
      <section className="hero-spotlight">
        <div className="mx-auto max-w-[1440px] px-4 py-16 sm:px-6 lg:px-10 lg:py-24" data-testid="why-pathways-section">
          <SectionHeader dark chapter="Chapter 02" eyebrow="The Advantage" title="Why Choose Pathway Programmes?" />
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {WHY_PATHWAYS.map((w, i) => (
              <Reveal key={w.title} delay={i * 0.08} className="h-full">
                <div className="h-full rounded-2xl border border-white/10 bg-white/5 p-7 backdrop-blur-sm transition-colors duration-300 hover:border-white/25" data-testid={`why-pathway-${w.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/10 text-dot-amber">
                    <Icon name={w.icon} className="h-5 w-5" />
                  </span>
                  <h3 className="mt-4 font-display text-lg font-semibold leading-snug text-white">{w.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-white/65">{w.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Enquiry form */}
      <section className="bg-sand" id="pathway-enquiry">
        <div className="mx-auto max-w-[1440px] scroll-mt-24 px-4 py-16 sm:px-6 lg:px-10 lg:py-24" data-testid="pathway-enquiry-section">
          <div className="mx-auto max-w-3xl">
            <SectionHeader
              chapter="Chapter 03"
              eyebrow="Enquiry"
              title="Start Your Pathway Journey"
              description="Tell us about your qualifications and goals — an advisor will map the right pathway for you."
            />
            <Reveal>
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-[0_8px_32px_rgba(27,47,91,0.06)] sm:p-10">
                <PathwayEnquiryForm />
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
}
